import type { FC, ReactElement } from 'react';
import { useEffect, useMemo, useState } from 'react';

import type { EventType, PopupPosition } from 'reactjs-popup/dist/types';

import { HoveredButton, Picture, Tooltip, Popover } from '@ui';
import { qrCode } from '@images';
import { observer } from 'mobx-react';
import useClipboard from '@customHooks/useClipboard';
import {
  TelegramShareButton,
  // ViberShareButton,
  // ViberIcon,
  VKShareButton,
  OKShareButton,
  // MailruShareButton,
  // MailruIcon,
  EmailShareButton
} from 'react-share'; // https://github.com/nygardk/react-share
import icons from '@icons';
import useWindowSize from '@customHooks/useWindowSize';
import { useStores } from '@root/src/stores';

const {
  copy: { black: copyBlackOutlined },
  email: { black: emailBlackOutlined },
  close: { darkGrey: closeDGOutlined },
  share: { black: shareBlackOutlined, white: shareWhiteOutlined }
} = icons.Outline;

const {
  share: { red: shareRedFilled },
  vkontakte: { ultraDG: vkontakteUltraDG },
  telegram: { ultraDG: telegramUltraDG },
  ok: { ultraDG: okUltraDG },
  max: { ultraDG: maxUltraDG }
} = icons.Fill;

export interface ShareSocialLinksNewProps {
  on?: EventType | EventType[];
  trigger?: ReactElement;
  position?: PopupPosition | PopupPosition[];
  link: string; // full link to share (with https://delikateska.ru/...)
  isShowQRCodeBtn?: boolean;
  offsetYDesktop?: number;
  buttonClassName?: string;
  iconWhite?: boolean;
  onOpenedStateChange?: (isOpen: boolean) => void;
}

const ShareSocialLinksNew: FC<ShareSocialLinksNewProps> = observer(
  ({
    on = ['hover', 'click'],
    trigger,
    position = 'top center',
    buttonClassName,
    link,
    isShowQRCodeBtn,
    offsetYDesktop,
    iconWhite,
    onOpenedStateChange
  }) => {
    const { modalsStore } = useStores();
    const [open, setOpen] = useState(false);

    const triggerComponent = useMemo(
      () =>
        trigger || (
          <HoveredButton
            wrapperClassName={buttonClassName}
            icon={iconWhite ? shareWhiteOutlined : shareBlackOutlined}
            iconHover={shareRedFilled}
            iconHeight={20}
            iconWidth={20}
          />
        ),
      [buttonClassName, iconWhite, trigger]
    );

    const { isMobile, isDesktop } = useWindowSize();

    const { changeQRCodeState } = modalsStore;

    useEffect(() => {
      if (onOpenedStateChange) {
        onOpenedStateChange(open);
      }
    }, [open, onOpenedStateChange]);

    const { copyPromocode: shareLink } = useClipboard({
      text: link,
      notificationText: 'Ссылка успешно скопирована!'
    });

    const openQRCode = (): void => {
      changeQRCodeState(link);
    };
    return (
      <Popover
        on={on}
        open={open}
        onOpen={() => {
          setOpen(true);
        }}
        onClose={() => {
          setOpen(false);
        }}
        nested
        trigger={<div className='max-w-max'>{triggerComponent}</div>}
        position={position}
        lockScroll
        closeOnDocumentClick
        mouseLeaveDelay={300}
        arrow={!isMobile}
        offsetY={isDesktop ? offsetYDesktop ?? 24 : 8}
        overlayStyle={{
          zIndex: 'var(--z-popover-overlay)',
          overflow: 'auto'
        }}
        contentStyle={{
          zIndex: 'var(--z-popover-content)',
          boxShadow:
            '0px 0px 2px 0px rgba(152, 162, 179, 0.15), 0px 0px 12px 0px rgba(152, 162, 179, 0.15)'
        }}
      >
        {(close: () => void) => (
          <>
            <div className='fromMd:hidden bg-black/20 left-0 top-0 fixed z-[var(--z-popover-backdrop)] w-full h-full backdrop-blur-sm' />
            <div
              onClick={() => {
                setOpen(false);
                close();
              }}
              className='sm:fixed sm:bottom-0 sm:left-0 sm:w-full p-4 bg-white sm:z-[var(--z-popover-content-mobile)] sm:rounded-t-lg fromMd:rounded-lg'
            >
              <div
                onClick={() => {
                  setOpen(false);
                  close();
                }}
                className='absolute fromMd:hidden top-2 right-2'
              >
                <Picture icon={closeDGOutlined} width='24' height='24' />
              </div>
              <div className='mb-4 text-sm font-medium text-center'>
                Поделиться
              </div>
              <div className='grid grid-cols-3 gap-5 '>
                {isShowQRCodeBtn && (
                  <Tooltip tooltipText='QRcode'>
                    <div
                      onClick={openQRCode}
                      className='bg-[var(--light-grey)] rounded-full mx-auto w-max p-1.5 h-max cursor-pointer'
                    >
                      <Picture icon={qrCode} height='24' width='24' />
                    </div>
                  </Tooltip>
                )}

                <Tooltip tooltipText='Скопировать ссылку в буфер обмена'>
                  <div
                    onClick={shareLink}
                    className='bg-[var(--light-grey)] rounded-full mx-auto w-max p-2.5 h-max cursor-pointer'
                  >
                    <Picture icon={copyBlackOutlined} height='20' width='20' />
                  </div>
                </Tooltip>

                <Tooltip tooltipText='Рассказать во Вконтакте'>
                  <VKShareButton url={link}>
                    <div className='bg-[var(--light-grey)] rounded-full mx-auto w-max p-2 h-max cursor-pointer'>
                      <Picture icon={vkontakteUltraDG} height='24' width='24' />
                    </div>
                  </VKShareButton>
                </Tooltip>

                <Tooltip tooltipText='Написать на стену в Одноклассниках'>
                  <OKShareButton url={link}>
                    <div className='bg-[var(--light-grey)] rounded-full mx-auto w-max p-2 h-max cursor-pointer'>
                      <Picture icon={okUltraDG} height='24' width='24' />
                    </div>
                  </OKShareButton>
                </Tooltip>

                <Tooltip tooltipText='Поделиться в Телеграм'>
                  <TelegramShareButton url={link}>
                    <div className='bg-[var(--light-grey)] rounded-full mx-auto w-max p-2 h-max cursor-pointer'>
                      <Picture icon={telegramUltraDG} height='24' width='24' />
                    </div>
                  </TelegramShareButton>
                </Tooltip>

                <Tooltip tooltipText='Поделиться в MAX'>
                  <a
                    href={`https://max.ru/:share?text=${encodeURIComponent(
                      link
                    )}`}
                    target='_blank'
                    rel='noreferrer'
                  >
                    <div className='bg-[var(--light-grey)] rounded-full mx-auto w-max p-2 h-max cursor-pointer'>
                      <Picture icon={maxUltraDG} height='24' width='24' />
                    </div>
                  </a>
                </Tooltip>
                {/* <Tooltip tooltipText='Поделиться в Моём мире'>
                    <MailruShareButton url={link}>
                      <MailruIcon size={40} round />
                    </MailruShareButton>
                  </Tooltip> */}
                {/*
                  <Tooltip tooltipText='Поделиться в Вайбер'>
                    <ViberShareButton url={link}>
                      <ViberIcon size={40} round />
                    </ViberShareButton>
                  </Tooltip> */}

                <Tooltip tooltipText='Отправить по электронный почте'>
                  <EmailShareButton url={link}>
                    <div className='bg-[var(--light-grey)] rounded-full mx-auto w-max p-2.5 h-max cursor-pointer'>
                      <Picture
                        icon={emailBlackOutlined}
                        height='20'
                        width='20'
                      />
                    </div>
                  </EmailShareButton>
                </Tooltip>
              </div>
            </div>
          </>
        )}
      </Popover>
    );
  }
);

export default ShareSocialLinksNew;
